// ============================================================
// HIT DAMAGE ROLL - The first half of one hit, before armour:
// the hit's context, the force field, the damage dice and the raw
// damage they add up to. Pure apart from the dice.
//
// DICE ORDER IS SACRED (gotcha #5): the 'A' damage type, the field's
// d100, the damage roll (and Tearing/Volatile's extra dice), the
// re-roll, a helpless target's second damage roll, Accurate's d10s, then
// Maximal's d10. E2E seeds that exact
// sequence, so a stage here must never draw earlier, later or twice.
// ============================================================
import { rollDiceExpression, rollDie, rollD100, diceRandom } from './dice';
import { onHitExposure } from './exposures';
import type { OnHitExposure } from './exposures';
import { hasWS, getWSP, applyTearing, applyMinMax, applyVolatile } from './weaponSpecials';
import { predicateBonuses } from './predicateDamage';
import { hitRuleBonuses } from './weaponRules';
import { activeProtagonist } from './survival/protagonist';
import type { Protagonist } from './survival/protagonist';
import { applyAverage, shouldAverage } from './survival/averagedDamage';
import { getBodyConfig, resolveArmourAP } from './bodyConfig';
import { effectiveVehicleFacing } from './vehicleFacing';
import type { Target, DiceResult } from '../types';
import type { CalcSingleHitParams, HitContext } from './hitDamageTypes';

const ANY_DAMAGE_TYPES = ['I', 'E', 'R', 'X'] as const;

/** Resolve what every stage shares. Draws ONE die when the weapon's damage type is 'A'. */
export function hitContext(params: CalcSingleHitParams): HitContext {
  const { slot, target, bodyType, protagonistRules = false, isMelee } = params;
  const effectiveBodyType = bodyType || target.bodyType || 'humanoid';
  const prot = activeProtagonist(target, protagonistRules);
  const bodyConfig = getBodyConfig(effectiveBodyType);
  // WHAT THE TARGET IS EXPOSED TO (v8.350.0) - read here, where the
  // target already is, so the roll and both forecasts agree for free.
  const exposed = onHitExposure(
    { conditions: target.conditions, exposures: target.exposures, teamTag: target.teamTag, sideTag: target.sideTag },
    { teamTag: params.attackerTeamTag, sideTag: params.attackerSideTag, isMelee },
  );
  const damageType = slot.damageType === 'A' ? ANY_DAMAGE_TYPES[Math.floor(diceRandom() * 4)] : slot.damageType;
  const isHorde = effectiveBodyType === 'horde' || target.horde?.isHorde || false;
  return { prot, bodyType: effectiveBodyType, bodyConfig, sp: slot.specialRules || [], exposed, damageType, isHorde };
}

/** The field's side of the hit, in the order `HitResult` reports it. */
export interface ForceFieldCheck {
  ffBlocked: boolean;
  ffRoll: number;
  ffOverloaded: boolean;
  ffProtection: number;
  ffOverloadThreshold: number;
}

/**
 * The rating the field rolls against. Fieldbreaker slips the field
 * entirely (no roll consumed, exactly like an inactive field, so the
 * seeded dice sequence stays aligned); Scrambler(X) degrades it.
 */
function fieldProtection(target: Target, sp: string[], exposed: OnHitExposure): number {
  const field = target.forceField;
  const rating = field?.active && field.protectionRating > 0 ? field.protectionRating : 0;
  if (hasWS(sp, 'Fieldbreaker') || exposed.ignoreField) return 0;
  return rating > 0 ? Math.max(0, rating - getWSP(sp, 'Scrambler')) : rating;
}

/**
 * The force field check (the field family, v8.283.0). Rolls a d100 only
 * when the field has a rating left. Overloader(X) widens the burnout
 * window when the field DOES catch the shot - but cannot invent one for
 * a field with no overload threshold.
 */
export function checkForceField(target: Target, sp: string[], exposed: OnHitExposure): ForceFieldCheck {
  const ffProtection = fieldProtection(target, sp, exposed);
  const ffOverloadThreshold = target.forceField?.active ? target.forceField.overloadThreshold || 0 : 0;
  const ffRoll = ffProtection > 0 ? rollD100() : 0;
  const ffBlocked = ffProtection > 0 && ffRoll <= ffProtection;
  const ffOverloaded = ffBlocked && ffOverloadThreshold > 0 && ffRoll <= ffOverloadThreshold + getWSP(sp, 'Overloader');
  return { ffBlocked, ffRoll, ffOverloaded, ffProtection, ffOverloadThreshold };
}

/**
 * Tearing, Proven, Primitive (applied before Accurate/re-roll for clean
 * base comparison), then Volatile - exploding on the tens the earlier
 * transforms leave standing (Primitive can cap them away, Proven can't
 * mint them). Graviton's flat replacement has no dice to explode.
 */
function shapeDice(roll: DiceResult, sp: string[]): DiceResult {
  let out = roll;
  if (hasWS(sp, 'Tearing')) out = applyTearing(out);
  if (hasWS(sp, 'Proven')) out = applyMinMax(out, getWSP(sp, 'Proven'), 'min');
  if (hasWS(sp, 'Primitive')) out = applyMinMax(out, getWSP(sp, 'Primitive'), 'max');
  if (hasWS(sp, 'Volatile')) out = applyVolatile(out);
  return out;
}

/**
 * The weapon's own roll. Graviton(X): damage = target AP at location × X
 * (replaces rolled damage - the dice are still rolled, so the sequence
 * holds). X defaults to 1 - a bare 'Graviton' and 'Graviton(0)' both
 * mean the unmultiplied original (rules-spec's multiplier, v8.197.6).
 */
function weaponRoll(params: CalcSingleHitParams, ctx: HitContext): DiceResult {
  const { slot, target, location } = params;
  const rolled = rollDiceExpression(slot.damageExpression);
  if (!hasWS(ctx.sp, 'Graviton')) return shapeDice(rolled, ctx.sp);
  const gravMult = Math.max(1, getWSP(ctx.sp, 'Graviton'));
  const armour = resolveArmourAP(target.armour, location.id, ctx.bodyConfig, effectiveVehicleFacing(target));
  const gravitonDmg = armour * gravMult;
  return shapeDice({ results: [{ type: 'flat' as const, value: gravitonDmg }], total: gravitonDmg }, ctx.sp);
}

/** Damage re-roll: roll twice, take the better base damage (Hunter of Aliens / Scourge of Heretics). */
function betterOfReroll(first: DiceResult, params: CalcSingleHitParams, sp: string[]): DiceResult {
  const { rerollDamage = false } = params;
  if (!rerollDamage || hasWS(sp, 'Graviton')) return first;
  const second = shapeDice(rollDiceExpression(params.slot.damageExpression), sp);
  return second.total > first.total ? second : first;
}

/**
 * A helpless target (engine/helpless): the damage is rolled TWICE and
 * added. The second roll is the weapon's own expression, shaped like the
 * first, drawn after the re-roll comparison and before Accurate's dice.
 * Graviton has no dice to roll again.
 */
function addHelplessRoll(roll: DiceResult, params: CalcSingleHitParams, sp: string[]): DiceResult {
  if (!params.helpless || hasWS(sp, 'Graviton')) return roll;
  const second = shapeDice(rollDiceExpression(params.slot.damageExpression), sp);
  return { results: [...roll.results, ...second.results], total: roll.total + second.total };
}

/** Accurate: +Xd10, pushed onto the roll IN PLACE (added after the re-roll comparison). */
function addAccurateDice(roll: DiceResult, count: number): void {
  for (let a = 0; a < count; a++) {
    const r = rollDie(10);
    roll.results.push({ type: 'dice', expr: '1d10', rolls: [r], total: r, sides: 10 });
    roll.total += r;
  }
}

/**
 * AVERAGED DAMAGE, at the same seam and before the clamp, so every die
 * that will be counted is included - the weapon's own, Accurate's, and
 * Tearing's extra. Tearing then buys nothing, which is right: what it
 * buys is variance, and variance is what this removes.
 *
 * THE DEFENDER'S OWN CLAMP, applied last so it catches every die - the
 * weapon's Proven/Primitive, and the Accurate dice. Per die, which is
 * what makes it the right lever: a krak missile loses three points to a
 * cap of 7 and a slugga loses one, so it tames the weapons that actually
 * kill and leaves small arms alone. Flat bonuses are untouched:
 * `applyMinMax` skips `flat` groups.
 */
function evenOut(roll: DiceResult, params: CalcSingleHitParams, prot: Protagonist | undefined): DiceResult {
  const { averagedDamage = 'off' } = params;
  let out = shouldAverage(averagedDamage, !!prot) ? applyAverage(roll) : roll;
  if (prot?.diceClampMin !== undefined) out = applyMinMax(out, prot.diceClampMin, 'min');
  if (prot?.diceClampMax !== undefined) out = applyMinMax(out, prot.diceClampMax, 'max');
  return out;
}

/** The hit's damage dice, every transform applied, and how many Accurate dice joined them. */
export function rollHitDice(
  params: CalcSingleHitParams,
  ctx: HitContext,
): { dmgRoll: DiceResult; accurateDice: number } {
  // Accurate: +Xd10 on first hit only.
  const accurateDice = params.accurateBonus > 0 && params.isFirstHit ? params.accurateBonus : 0;
  const roll = addHelplessRoll(betterOfReroll(weaponRoll(params, ctx), params, ctx.sp), params, ctx.sp);
  addAccurateDice(roll, accurateDice);
  return { dmgRoll: evenOut(roll, params, ctx.prot), accurateDice };
}

/** The raw damage and the components the breakdown reports, in `HitResult`'s field names. */
export interface RawDamage {
  rawDamage: number;
  rawReduction: number;
  strengthBonus: number;
  talentDmgBonus: number;
  maximalDmgBonus: number;
  predicate: { dmg: number; pen: number };
  exposureDmg: number;
}

// Devastating's homebrew melee damage rider (+X per DoS past the first)
// retired in v8.197.13 - the official rule is anti-horde Magnitude,
// applied in postHitEffects. devastatingBonus stays a permanent 0 in
// the breakdown so old logs still render.
export const DEVASTATING_BONUS = 0;
// Official DW Scatter (v8.197.11): the ±3 damage swings are gone - the
// Point Blank payoff moved to extra hits (calcTotalHits), and distance
// shows up as the pellets losing punch: worn armour counts DOUBLE at
// Long/Extreme (see hitSoak). scatterMod stays in the breakdown as a
// permanent 0 so old logs still render.
export const SCATTER_MOD = 0;

type Falloff = NonNullable<CalcSingleHitParams['falloff']>;
const FALLOFF: Record<string, (raw: number, value: number, leap: number) => number> = {
  percent: (raw, value, leap) => Math.floor(raw * Math.pow(value / 100, leap)),
  flat: (raw, value, leap) => raw - value * leap,
  fixed: (raw, value) => Math.floor(raw * (value / 100)),
};

/**
 * THE RAW SEAM (v8.372.3): an arc echo's falloff, then a passed avoid
 * test's half or quarter - both on the roll, BEFORE cover and armour, so
 * a faint echo still meets the plate (the user's ruling).
 */
function scaleRaw(raw: number, falloff: Falloff | undefined, rawScale: number | undefined): number {
  let out = raw;
  if (falloff && falloff.leap > 0) {
    const { mode, value, floor, leap } = falloff;
    const scaled = (FALLOFF[mode] ?? FALLOFF.fixed)(out, Math.max(0, value), leap);
    out = Math.max(Math.max(0, floor), scaled);
  }
  if (rawScale !== undefined && rawScale < 1) out = Math.floor(out * Math.max(0, rawScale));
  return out;
}

/**
 * The weapon rules left to each hit (plan armoury, `weaponRules.hitRuleBonuses`), added to the predicate pair:
 * their damage here, their Pen where the predicate's goes (`soakOf`). The pair itself when there are none.
 */
function withHitRules(predicate: { dmg: number; pen: number }, params: CalcSingleHitParams, ctx: HitContext) {
  const rules = params.slot.hitRules;
  if (!rules?.length) return predicate;
  const { target, location } = params;
  const armour = resolveArmourAP(target.armour, location.id, ctx.bodyConfig, effectiveVehicleFacing(target));
  const ruled = hitRuleBonuses(rules, { location: location.id, armour });
  return { dmg: predicate.dmg + ruled.dmg, pen: predicate.pen + ruled.pen };
}

/** Raw damage: the dice plus every flat share, floored at 0, then the raw seam. Maximal draws its d10 here. */
export function rawDamageOf(params: CalcSingleHitParams, ctx: HitContext, dmgRoll: DiceResult): RawDamage {
  const { slot, target, isMelee, isFirstHit, runningWounds, damageBonusMelee = 0, damageBonusRanged = 0 } = params;
  const strengthBonus = isMelee ? slot.strengthBonus || 0 : 0;
  const talentDmgBonus = isMelee ? damageBonusMelee : damageBonusRanged;
  // Official Maximal: +1d10 damage (rolled, replacing the flat +10 of
  // v8.197.7 and earlier), still one overcharged discharge = first hit only.
  const maximalDmgBonus = hasWS(ctx.sp, 'Maximal') && isFirstHit ? rollDie(10) : 0;
  // The predicate family (v8.285.0): who is being hit, and where the
  // fight stands, in one table - dmg here, pen in hitSoak.
  const hitIndex = params.hitIndex ?? (isFirstHit ? 0 : 1);
  const predicate = withHitRules(predicateBonuses(ctx.sp, { target, runningWounds, hitIndex }), params, ctx);
  const exposureDmg = ctx.exposed.dmg + (ctx.exposed.dmgByType[ctx.damageType as 'E' | 'I' | 'R' | 'X'] ?? 0);
  const unscaled = Math.max(
    0,
    dmgRoll.total +
      strengthBonus +
      talentDmgBonus +
      DEVASTATING_BONUS +
      maximalDmgBonus +
      SCATTER_MOD +
      predicate.dmg +
      exposureDmg,
  );
  const rawDamage = scaleRaw(unscaled, params.falloff, params.rawScale);
  const rawReduction = unscaled - rawDamage;
  return { rawDamage, rawReduction, strengthBonus, talentDmgBonus, maximalDmgBonus, predicate, exposureDmg };
}
